import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import axiosInstance from "../../../config/axiosApi/axiosApi";
import APIs from "../../../Constants/apis";
import showErrorToast from "../toast/toast";
import CustomModal from "../modal/customModal";
import useDarkMode from "../../../hooks/useDarkMode";
import useThemeColor from "../../../hooks/useThemeColor";

const BranchAssignmentSelector = ({
  selectedBranches,
  setSelectedBranches,
  isOpen,
  onClose,
  productId = null,
  parentCompID = null,
  onSubmitSelection = null,
}) => {
  const CompID = useSelector((state) => state.auth.user)?.userData?.compID;
  const effectiveCompID = parentCompID || CompID; // Use parentCompID if provided, otherwise use Redux CompID
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedBranchIds, setSelectedBranchIds] = useState([]);
  const [savingSelection, setSavingSelection] = useState(false);
  const hasAutoSelectedRef = useRef(false); // Track if we've auto-selected branches
  const isInitializedRef = useRef(false); // Track if component has been initialized with prop data
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();

  const fetchBranches = useCallback(async () => {
    try {
      setLoading(true);
      let branchData = [];
      let response = null;
      
      // Try GET_BRANCHES first (uses authenticated user's CompID)
      try {
        response = await axiosInstance.get(APIs.GET_BRANCHES);
        console.log("GET_BRANCHES response:", response.data);
        
        // Handle response structure
        if (response.data) {
          if (response.data.status && response.data.data) {
            branchData = Array.isArray(response.data.data) ? response.data.data : [];
          } else if (Array.isArray(response.data)) {
            branchData = response.data;
          } else if (response.data.data && Array.isArray(response.data.data)) {
            branchData = response.data.data;
          }
        }
        
        // Filter to only show child branches (where ParentComID equals parent company)
        if (branchData.length > 0 && effectiveCompID) {
          const toInt = (v) => {
            if (v === null || v === undefined) return null;
            const n = typeof v === "number" ? v : parseInt(v, 10);
            return Number.isFinite(n) ? n : null;
          };
          const effectiveCompIdInt = toInt(effectiveCompID);
          branchData = branchData.filter((b) => {
            const parentId = toInt(b?.ParentComID ?? b?.parentComID);
            const branchId = toInt(b?.CompID ?? b?.compID);
            return effectiveCompIdInt !== null
              && parentId === effectiveCompIdInt
              && branchId !== null
              && branchId !== effectiveCompIdInt;
          });
        }
      } catch (branchErr) {
        console.warn("GET_BRANCHES failed, trying GET_COMPANY_LOCATIONS:", branchErr);
        
        // Fallback to GET_COMPANY_LOCATIONS if GET_BRANCHES fails
        if (effectiveCompID) {
          try {
            response = await axiosInstance.get(APIs.GET_COMPANY_LOCATIONS, {
              params: { CompID: effectiveCompID },
            });
           
            if (response.data) {
              const locationsData = response.data.data || response.data;
              branchData = Array.isArray(locationsData) 
                ? locationsData.filter((b) => {
                        const toInt = (v) => {
                          if (v === null || v === undefined) return null;
                          const n = typeof v === "number" ? v : parseInt(v, 10);
                          return Number.isFinite(n) ? n : null;
                        };
                        const effectiveCompIdInt = toInt(effectiveCompID);
                        const parentId = toInt(b?.ParentComID ?? b?.parentComID);
                        const branchId = toInt(b?.CompID ?? b?.compID);
                        return effectiveCompIdInt !== null
                          && parentId === effectiveCompIdInt
                          && branchId !== null
                          && branchId !== effectiveCompIdInt;
                  })
                : [];
            }
          } catch (locationErr) {
            console.error("GET_COMPANY_LOCATIONS also failed:", locationErr);
            throw locationErr;
          }
        }
      }
 
      setBranches(branchData);
      
        // By default, select all branches for new products (when productId is null)
        // Only auto-select once when modal opens for new products
        if (branchData.length > 0 && !productId && !hasAutoSelectedRef.current && !isInitializedRef.current) {
          const currentSelectedCount = selectedBranches?.length || 0;
          if (currentSelectedCount === 0) {
            const allBranchIds = branchData
              .map((b) => {
                const id = b?.CompID ?? b?.compID;
                const n = typeof id === "number" ? id : parseInt(id, 10);
                return Number.isFinite(n) ? n : null;
              })
              .filter((id) => id !== null);
            setSelectedBranchIds(allBranchIds);
            setSelectedBranches(branchData);
            hasAutoSelectedRef.current = true;
            isInitializedRef.current = true; // Mark as initialized after auto-selecting
          }
        }

      if (branchData.length === 0) {
        console.warn("No branches found for parent company");
      }
    } catch (err) {
      console.error("Error fetching branches:", err);
      console.error("Error response:", err.response);
      const errorMessage = err.response?.data?.message || err.message || "Unknown error occurred";
      showErrorToast("Failed to load branches: " + errorMessage);
      setBranches([]);
    } finally {
      setLoading(false);
    }
  }, [effectiveCompID, productId, selectedBranches]);

  const fetchAssignedBranches = useCallback(async () => {
    if (!productId || !parentCompID) return;

    try {
      const response = await axiosInstance.get(
        `${APIs.GET_ASSIGNED_BRANCHES_FOR_PRODUCT}/${productId}/${parentCompID}`
      );

      if (response.data && response.data.data) {
        const assignedBranches = response.data.data
          .filter((a) => a.isAssigned)
          .map((a) => ({
            CompID: a.branchCompID ?? a.BranchCompID,
            CompName:
              a.branchName ??
              a.BranchName ??
              a.compName ??
              a.CompName ??
              `Branch ${a.branchCompID ?? a.BranchCompID}`,
          }));
        const assignedIds = assignedBranches.map(a => a.CompID);
        setSelectedBranchIds(assignedIds);
        setSelectedBranches(assignedBranches);
        isInitializedRef.current = true; // Mark as initialized after loading assigned branches
      }
    } catch (err) {
      // Silent fail - product might not have assignments yet
    }
  }, [productId, parentCompID]);

  useEffect(() => {
    if (isOpen && !isInitializedRef.current) {
      // Reset auto-select flag when modal opens
      if (!productId) {
        hasAutoSelectedRef.current = false;
      }
      
      // Sync selectedBranchIds from prop ONLY when modal first opens
      if (selectedBranches && Array.isArray(selectedBranches) && selectedBranches.length > 0) {
        const ids = selectedBranches.map((b) => (typeof b === 'number' ? b : b.CompID || b.branchCompID || b.compID));
        setSelectedBranchIds(ids);
        isInitializedRef.current = true;
      }
      
      fetchBranches();
      // Only fetch assigned branches if editing an existing product
      if (productId && parentCompID) {
        fetchAssignedBranches();
      }
    } else if (!isOpen) {
      // Reset initialization flag when modal closes
      isInitializedRef.current = false;
    }
  }, [isOpen, fetchBranches, productId, parentCompID, fetchAssignedBranches, selectedBranches]);

  const handleBranchToggle = (branchId) => {
    setSelectedBranchIds((prev) => {
      const newIds = prev.includes(branchId)
        ? prev.filter((id) => id !== branchId)
        : [...prev, branchId];
      
      // Update selectedBranches to match - this will trigger prop change but we won't sync it back
      const updatedBranches = branches.filter((b) => {
        const id = b.CompID || b.compID;
        return newIds.includes(id);
      });
      
      // Update parent state without triggering our useEffect
      setSelectedBranches(updatedBranches);
      
      return newIds;
    });
  };

  const handleSelectAll = () => {
    if (selectedBranchIds.length === branches.length) {
      setSelectedBranchIds([]);
      setSelectedBranches([]);
    } else {
      const allBranchIds = branches
        .map((b) => {
          const id = b?.CompID ?? b?.compID;
          const n = typeof id === "number" ? id : parseInt(id, 10);
          return Number.isFinite(n) ? n : null;
        })
        .filter((id) => id !== null);
      setSelectedBranchIds(allBranchIds);
      setSelectedBranches(branches);
    }
  };

  const handleSave = async () => {
    const selected = branches.filter((b) => {
      const branchId = b.CompID || b.compID;
      return selectedBranchIds.includes(branchId);
    });
    setSelectedBranches(selected);
    if (typeof onSubmitSelection === "function") {
      try {
        setSavingSelection(true);
        const success = await onSubmitSelection();
        if (success && onClose) onClose();
      } finally {
        setSavingSelection(false);
      }
      return;
    }
    if (onClose) onClose();
  };

  const renderBranchItem = (branch, index) => {
    const branchId = branch.CompID || branch.compID;
    const isSelected = selectedBranchIds.includes(branchId);

    return (
      <div
        key={branchId}
        className={`branch-item ${isSelected ? 'selected' : ''}`}
        onClick={() => handleBranchToggle(branchId)}
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '12px 16px',
          borderBottom: `1px solid ${isDarkMode ? '#404040' : '#e9ecef'}`,
          cursor: 'pointer',
          backgroundColor: isSelected
            ? (isDarkMode ? '#2d3436' : '#f8f9fa')
            : (isDarkMode ? '#1a1a1a' : '#ffffff'),
          transition: 'all 0.2s ease',
          borderRadius: isSelected ? '8px' : '0',
          margin: isSelected ? '4px 0' : '0',
          border: isSelected ? `2px solid ${isDarkMode ? '#555' : '#dee2e6'}` : 'none'
        }}
        onMouseEnter={(e) => {
          if (!isSelected) {
            e.currentTarget.style.backgroundColor = isDarkMode ? '#2d3436' : '#f8f9fa';
          }
        }}
        onMouseLeave={(e) => {
          if (!isSelected) {
            e.currentTarget.style.backgroundColor = isDarkMode ? '#1a1a1a' : '#ffffff';
          }
        }}
      >
        {/* Custom Checkbox */}
        <div
          style={{
            width: '20px',
            height: '20px',
            borderRadius: '4px',
            border: isSelected 
              ? `2px solid ${themeColor}` 
              : `2px solid ${isDarkMode ? '#555' : '#dee2e6'}`,
            background: isSelected 
              ? themeColor 
              : 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: '12px',
            flexShrink: 0,
            transition: 'all 0.2s ease'
          }}
        >
          {isSelected && (
            <i 
              className="fa fa-check" 
              style={{ 
                fontSize: '12px', 
                color: 'white' 
              }}
            ></i>
          )}
        </div>

        {/* Branch Icon */}
        <div
          style={{
            width: '40px',
            height: '40px',
            background: isDarkMode ? '#555' : '#f8f9fa',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: '12px',
            border: isDarkMode ? '1px solid #666' : '1px solid #e9ecef',
            flexShrink: 0
          }}
        >
          <i className="fa fa-building" style={{ fontSize: '16px', color: isDarkMode ? '#ffffff' : '#6c757d' }}></i>
        </div>

        {/* Branch Name */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontWeight: '500',
            fontSize: '14px',
            color: isDarkMode ? '#ffffff' : '#495057',
            marginBottom: '2px'
          }}>
            {branch.CompName || branch.compName || `Branch ${branchId}`}
          </div>
        </div>

        {/* Selection Indicator */}
        {isSelected && (
          <div style={{ marginLeft: '12px', flexShrink: 0 }}>
            <div
              style={{
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                backgroundColor: '#28a745',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <i className="fa fa-check" style={{ fontSize: '10px', color: 'white' }}></i>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <CustomModal
      title="Assign Product to Branches"
      subtitle="Select the branches where this product should be available"
      isOpen={isOpen}
      onClose={onClose}
      isCentered={true}
      showButton={true}
      showSubmitButton={true}
      showCancelButton={true}
      submitButtonText={savingSelection ? "Saving..." : "Save"}
      submitButtonDisabled={savingSelection}
      onSubmit={handleSave}
      modalSize="modal-lg"
    >
      {loading ? (
        <div className="text-center py-4">
          <div className="spinner-border" role="status" style={{ color: isDarkMode ? '#ffffff' : '#6c757d' }}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <div style={{ color: isDarkMode ? '#ffffff' : '#6c757d', marginTop: '8px' }}>
            Loading branches...
          </div>
        </div>
      ) : (
        <>
          {/* Select All Button */}
          {branches.length > 0 && (
            <div style={{ padding: '16px', borderBottom: `1px solid ${isDarkMode ? '#404040' : '#e9ecef'}` }}>
              <button
                type="button"
                onClick={handleSelectAll}
                style={{
                  background: selectedBranchIds.length === branches.length 
                    ? (isDarkMode ? '#dc3545' : '#dc3545')
                    : themeColor,
                  border: 'none',
                  borderRadius: '8px',
                  padding: '8px 16px',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = `0 4px 12px ${selectedBranchIds.length === branches.length ? 'rgba(220, 53, 69, 0.3)' : themeColor + '40'}`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <i 
                  className={`fa ${selectedBranchIds.length === branches.length ? 'fa-times' : 'fa-check-square'}`} 
                  style={{ fontSize: '12px' }}
                ></i>
                {selectedBranchIds.length === branches.length
                  ? "Deselect All"
                  : "Select All"}
              </button>
            </div>
          )}

          {/* Branches List */}
          <div style={{
            maxHeight: '400px',
            overflowY: 'auto',
            backgroundColor: isDarkMode ? '#1a1a1a' : '#ffffff'
          }}>
            {branches.length === 0 ? (
              <div className="text-center py-4">
                <div style={{
                  width: '48px',
                  height: '48px',
                  background: isDarkMode ? '#555' : '#f8f9fa',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                  border: isDarkMode ? '1px solid #666' : '1px solid #e9ecef'
                }}>
                  <i className="fa fa-building" style={{ fontSize: '20px', color: isDarkMode ? '#ffffff' : '#6c757d' }}></i>
                </div>
                <p style={{ color: isDarkMode ? '#ffffff' : '#6c757d', marginBottom: '0' }}>
                  No child branches available
                </p>
              </div>
            ) : (
              <div>
                {branches.map((branch, index) => renderBranchItem(branch, index))}
              </div>
            )}
          </div>

          {/* Selection Summary */}
          {selectedBranchIds.length > 0 && (
            <div style={{
              padding: '16px',
              borderTop: `1px solid ${isDarkMode ? '#404040' : '#e9ecef'}`,
              backgroundColor: isDarkMode ? '#2d3436' : '#f8f9fa'
            }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{
                  width: '24px',
                  height: '24px',
                  background: '#28a745',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '8px',
                  flexShrink: 0
                }}>
                  <i className="fa fa-check" style={{ fontSize: '12px', color: 'white' }}></i>
                </div>
                <div>
                  <div style={{
                    fontWeight: '500',
                    fontSize: '14px',
                    color: isDarkMode ? '#ffffff' : '#495057'
                  }}>
                    {selectedBranchIds.length} {selectedBranchIds.length === 1 ? 'branch' : 'branches'} selected
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </CustomModal>
  );
};

export default BranchAssignmentSelector;
